// ProviderTrackingScreen.jsx

import React, {
  useEffect,
  useState,
} from "react";

import "./ProviderTracking.css";

import { toast } from "react-toastify";

import Pagination from "../activity/pagination";

import AddProviderTracking from "./AddProviderTracking";
import AddSessionModal from "./AddProviderTrackingSession";
import AddEditSessionModal from "./AddEditSessionModal";

import {
  getProviderTrackingSummary,
  updateProviderTrackingStatus,
} from "../../config/LiveApi";

const ProviderTrackingScreen = () => {
  const [trackingList, setTrackingList] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [currentPage, setCurrentPage] =
    useState(1);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [pagination, setPagination] =
    useState({});

  const [isAddModalOpen, setIsAddModalOpen] =
    useState(false);

  const [expandedRows, setExpandedRows] =
    useState({});

  const [
    isSessionModalOpen,
    setIsSessionModalOpen,
  ] = useState(false);

  const [
    selectedTrackingId,
    setSelectedTrackingId,
  ] = useState("");

  const [
    editSessionModal,
    setEditSessionModal,
  ] = useState(false);

  const [
    selectedSessionData,
    setSelectedSessionData,
  ] = useState(null);

  const fetchProviderTracking = async (
    page,
    search,
  ) => {
    try {
      setLoading(true);

      const response =
        await getProviderTrackingSummary(
          page,
          search,
        );

      setTrackingList(response?.data || []);

      setPagination(
        response?.pagination || {},
      );

      setLoading(false);
    } catch (error) {
      setLoading(false);

      toast.error(
        "Unable to fetch provider tracking summary.",
      );
    }
  };

  const handleStatusChange = async (
    id,
    currentStatus,
  ) => {
    try {
      const newStatus =
        currentStatus === "ACTIVE"
          ? "INACTIVE"
          : "ACTIVE";

      await updateProviderTrackingStatus(id, {
        status: newStatus,
      });

      toast.success(
        `Status updated to ${newStatus}`,
      );

      fetchProviderTracking(
        currentPage,
        searchTerm,
      );
    } catch (error) {
      toast.error(
        "Failed to update status",
      );
    }
  };

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getRunningDays = (
    startDate,
    endDate,
  ) => {
    const start = new Date(startDate);

    const end = endDate
      ? new Date(endDate)
      : new Date();

    const diff =
      end.getTime() - start.getTime();

    return Math.ceil(
      diff / (1000 * 60 * 60 * 24),
    );
  };

  const handleEditSession = (
    trackingId,
    session,
  ) => {
    setSelectedTrackingId(
      trackingId,
    );

    setSelectedSessionData(
      session,
    );

    setEditSessionModal(true);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchProviderTracking(
        currentPage,
        searchTerm,
      );
    }, 500);

    return () =>
      clearTimeout(delayDebounce);
  }, [currentPage, searchTerm]);

  return (
    <div className="provider-tracking-container">
      <div className="provider-tracking-header-wrapper">
        <div className="provider-tracking-header">
          <h2 className="provider-tracking-title">
            Provider Tracking Summary
          </h2>

          <input
            className="provider-tracking-search-input"
            type="text"
            placeholder="Search org / provider..."
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);

              setSearchTerm(
                e.target.value,
              );
            }}
          />
        </div>

        <button
          className="provider-tracking-add-button"
          onClick={() =>
            setIsAddModalOpen(true)
          }
        >
          Add +
        </button>
      </div>

      <table className="provider-tracking-table">
        <thead>
          <tr>
            <th>Org Name</th>

            <th>Provider</th>

            <th>Type</th>

            <th>Current Running</th>

            <th>Status</th>

            <th>Created Date</th>

            <th>Actions</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7">
                <div className="provider-tracking-loading-state">
                  Loading...
                </div>
              </td>
            </tr>
          ) : trackingList?.length > 0 ? (
            trackingList.map(
              (item, index) => {
                const firstRange =
                  item?.txnDateRanges?.[0];

                return (
                  <React.Fragment
                    key={index}
                  >
                    <tr>
                      <td>
                        {item?.orgName ||
                          "-"}
                      </td>

                      <td>
                        {item?.pg || "-"}
                      </td>

                      <td>
                        {item?.type || "-"}
                      </td>

                      <td>
                        {firstRange ? (
                          <div className="provider-tracking-main-session">
                            <div className="provider-tracking-session-header">
                              <div className="provider-tracking-session-title">
                                Session 1
                              </div>

                              <button
                                className="provider-tracking-edit-session-btn"
                                onClick={() =>
                                  handleEditSession(
                                    item?._id,
                                    firstRange,
                                  )
                                }
                              >
                                Edit
                              </button>
                            </div>

                            <div className="provider-tracking-date-row">
                              <span>
                                {new Date(
                                  firstRange?.startDate,
                                ).toLocaleDateString(
                                  "en-GB",
                                )}
                              </span>

                              <span>
                                →
                              </span>

                              <span>
                                {firstRange?.endDate
                                  ? new Date(
                                      firstRange?.endDate,
                                    ).toLocaleDateString(
                                      "en-GB",
                                    )
                                  : "Current Date"}
                              </span>
                            </div>

                            <div className="provider-tracking-running-days">
                              Running:
                              {" "}
                              {getRunningDays(
                                firstRange?.startDate,
                                firstRange?.endDate,
                              )}{" "}
                              Days
                            </div>

                            {firstRange?.remark && (
                              <div className="provider-tracking-range-remark">
                                {
                                  firstRange?.remark
                                }
                              </div>
                            )}

                            {item
                              ?.txnDateRanges
                              ?.length >
                              1 && (
                              <button
                                className="provider-tracking-expand-btn"
                                onClick={() =>
                                  toggleExpand(
                                    item?._id,
                                  )
                                }
                              >
                                {expandedRows[
                                  item?._id
                                ]
                                  ? "Hide More"
                                  : `+${
                                      item
                                        ?.txnDateRanges
                                        ?.length - 1
                                    } More`}
                              </button>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td>
                        <span
                          className={`provider-tracking-status-badge ${
                            item?.status ===
                            "ACTIVE"
                              ? "provider-tracking-active-status"
                              : "provider-tracking-inactive-status"
                          }`}
                        >
                          {item?.status}
                        </span>
                      </td>

                      <td>
                        {new Date(
                          item?.createdAt,
                        ).toLocaleString(
                          "en-GB",
                          {
                            day: "2-digit",
                            month:
                              "2-digit",
                            year:
                              "numeric",
                            hour:
                              "2-digit",
                            minute:
                              "2-digit",
                            hour12:
                              false,
                          },
                        )}
                      </td>

                      <td>
                        <div className="provider-tracking-action-wrapper">
                          <button
                            className={`provider-tracking-action-btn ${
                              item?.status ===
                              "ACTIVE"
                                ? "provider-tracking-deactivate-btn"
                                : "provider-tracking-activate-btn"
                            }`}
                            onClick={() =>
                              handleStatusChange(
                                item?._id,
                                item?.status,
                              )
                            }
                          >
                            {item?.status ===
                            "ACTIVE"
                              ? "Deactivate"
                              : "Activate"}
                          </button>

                          <button
                            className="provider-tracking-add-session-btn"
                            onClick={() => {
                              setSelectedTrackingId(
                                item?._id,
                              );

                              setIsSessionModalOpen(
                                true,
                              );
                            }}
                          >
                            + Add Session
                          </button>
                        </div>
                      </td>
                    </tr>

                    {expandedRows[
                      item?._id
                    ] && (
                      <tr>
                        <td
                          colSpan="7"
                          className="provider-tracking-expanded-wrapper"
                        >
                          <div className="provider-tracking-expanded-container">
                            {item?.txnDateRanges
                              ?.slice(1)
                              ?.map(
                                (
                                  range,
                                  idx,
                                ) => (
                                  <div
                                    key={
                                      idx
                                    }
                                    className="provider-tracking-expanded-card"
                                  >
                                    <div className="provider-tracking-session-header">
                                      <div className="provider-tracking-session-title">
                                        Session{" "}
                                        {idx +
                                          2}
                                      </div>

                                      <button
                                        className="provider-tracking-edit-session-btn"
                                        onClick={() =>
                                          handleEditSession(
                                            item?._id,
                                            range,
                                          )
                                        }
                                      >
                                        Edit
                                      </button>
                                    </div>

                                    <div className="provider-tracking-date-row">
                                      <span>
                                        {new Date(
                                          range?.startDate,
                                        ).toLocaleDateString(
                                          "en-GB",
                                        )}
                                      </span>

                                      <span>
                                        →
                                      </span>

                                      <span>
                                        {range?.endDate
                                          ? new Date(
                                              range?.endDate,
                                            ).toLocaleDateString(
                                              "en-GB",
                                            )
                                          : "Current Date"}
                                      </span>
                                    </div>

                                    <div className="provider-tracking-running-days">
                                      Running:
                                      {" "}
                                      {getRunningDays(
                                        range?.startDate,
                                        range?.endDate,
                                      )}{" "}
                                      Days
                                    </div>

                                    {range?.remark && (
                                      <div className="provider-tracking-range-remark">
                                        {
                                          range?.remark
                                        }
                                      </div>
                                    )}
                                  </div>
                                ),
                              )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              },
            )
          ) : (
            <tr>
              <td colSpan="7">
                <div className="provider-tracking-empty-state">
                  No data found.
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Pagination
        currentPage={currentPage}
        setCurrentPage={
          setCurrentPage
        }
        totalPages={
          pagination?.totalPages || 1
        }
      />

      {isAddModalOpen && (
        <AddProviderTracking
          onClose={() =>
            setIsAddModalOpen(false)
          }
          onSuccess={() =>
            fetchProviderTracking(
              currentPage,
              searchTerm,
            )
          }
        />
      )}

      {isSessionModalOpen && (
        <AddSessionModal
          selectedId={
            selectedTrackingId
          }
          onClose={() =>
            setIsSessionModalOpen(false)
          }
          onSuccess={() =>
            fetchProviderTracking(
              currentPage,
              searchTerm,
            )
          }
        />
      )}

      {editSessionModal && (
        <AddEditSessionModal
          isEdit={true}
          selectedId={
            selectedTrackingId
          }
          sessionData={
            selectedSessionData
          }
          onClose={() =>
            setEditSessionModal(
              false,
            )
          }
          onSuccess={() =>
            fetchProviderTracking(
              currentPage,
              searchTerm,
            )
          }
        />
      )}
    </div>
  );
};

export default ProviderTrackingScreen;